import ScrollReveal from "@/components/base/ScrollReveal";
import { clinicalTeam, formatWorksWith, therapistProfilePath } from "@/data/therapists";
import TeamPortraitStage from "@/components/base/TeamPortraitStage";

const featuredTherapistIds = ["vanessa-braithwaite", "sathuu-sivakumaran", "kinder-bradley"];
const therapists = featuredTherapistIds
  .map((id) => clinicalTeam.find((member) => member.id === id))
  .filter((member): member is NonNullable<typeof member> => Boolean(member));

export default function Therapists() {
  return (
    <section
      id="therapists"
      className="w-full py-14 md:py-20"
      style={{ background: "linear-gradient(180deg, #F2EEE4 0%, #EEE8DA 45%, #F5F1E8 100%)" }}
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <ScrollReveal variant="fadeIn" delay={0}>
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-5 h-[2px] rounded-full"
                  style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }}
                />
                <span
                  className="font-manrope text-[13px] font-semibold tracking-widest uppercase"
                  style={{ color: "#3C6FF8" }}
                >
                  Featured Therapists
                </span>
              </div>
            </ScrollReveal>
            <ScrollReveal variant="fadeUp" delay={80}>
              <h2 className="font-fraunces text-[#2B2B2B] text-4xl md:text-5xl font-light leading-snug">
                Meet the people<br />
                <em className="not-italic" style={{ color: "#7D54FC" }}>you&apos;ll actually work with</em>
              </h2>
            </ScrollReveal>
          </div>
          <ScrollReveal variant="fadeIn" delay={160}>
            <a
              href="/therapists"
              className="inline-flex items-center gap-1.5 font-manrope text-[15px] font-medium transition-colors cursor-pointer whitespace-nowrap link-arrow"
              style={{ color: "#3C6FF8" }}
              onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.color = "#2a5de6"}
              onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.color = "#3C6FF8"}
            >
              View all Therapists <i className="ri-arrow-right-line"></i>
            </a>
          </ScrollReveal>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7">
          {therapists.map((t, i) => (
            <ScrollReveal key={t.name} variant="fadeUp" delay={i * 110} threshold={0.08}>
              <div
                className="bg-white/80 border rounded-2xl overflow-hidden flex flex-col h-full card-hover"
                style={{ borderColor: t.accentColor + "22" }}
              >
                {/* Colored top bar */}
                <div className="h-[3px] w-full" style={{ background: `linear-gradient(90deg, ${t.accentColor}, ${t.accentColor}88)` }} />

                <TeamPortraitStage src={t.img} name={t.name} variant="card">
                  <div className="absolute top-3 right-3">
                    <span
                      className="font-manrope text-[10px] font-semibold px-2.5 py-1 rounded-full tracking-wide"
                      style={
                        t.availability === "Accepting clients"
                          ? { background: "rgba(107,116,47,0.15)", color: "#6B742F" }
                          : { background: "rgba(43,43,43,0.10)", color: "rgba(43,43,43,0.6)" }
                      }
                    >
                      {t.availability}
                    </span>
                  </div>
                </TeamPortraitStage>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium mb-0.5">
                    {t.name},&nbsp;<span className="font-manrope text-sm font-normal text-[#2B2B2B]/50">{t.credentials}</span>
                  </h3>
                  <p className="font-manrope text-[13px] font-semibold mb-2" style={{ color: t.accentColor }}>{t.role}</p>
                  <p className="font-manrope text-[13px] mb-2 font-semibold" style={{ color: t.accentColor }}>{t.price}</p>

                  {/* Focus tags inline */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {t.specialties.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="font-manrope text-[12px] font-semibold px-2.5 py-0.5 rounded-full"
                        style={{ color: t.tagColor, background: t.tagBg }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {t.worksWith && t.worksWith.length > 0 && (
                    <p className="font-manrope text-[#2B2B2B]/65 text-[12.5px] font-semibold leading-relaxed mb-3">
                      Works with: {formatWorksWith(t.worksWith)}
                    </p>
                  )}

                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-5 flex-1 line-clamp-4">
                    {t.shortBio}
                  </p>

                  <a
                    href={therapistProfilePath(t.id)}
                    className="inline-flex items-center justify-center gap-2 font-manrope text-[15px] font-semibold px-5 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap btn-outline mt-auto"
                    style={{ border: `1.5px solid ${t.accentColor}55`, color: t.accentColor }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = t.accentColor;
                      (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                      (e.currentTarget as HTMLAnchorElement).style.borderColor = t.accentColor;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                      (e.currentTarget as HTMLAnchorElement).style.color = t.accentColor;
                      (e.currentTarget as HTMLAnchorElement).style.borderColor = t.accentColor + "55";
                    }}
                  >
                    Learn About {t.name.split(" ")[0]}
                  </a>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
